// Palindromes — Basic vs Optimized code for every "Beginner Problems" drill listed in the
// Palindromes Explanation modal. Same shape as matrixDrills.js: concise steps + an interactive
// "watch it happen" visualizer per version. Two drills cross-reference curated problems.

const palindromeDrills = [
  {
    id: 'valid-palindrome',
    title: 'Valid Palindrome',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Build a Cleaned String, Then Compare to Its Reverse',
        explanation: 'Strip out every non-alphanumeric character and lowercase everything into a brand new string, then compare that string to its own reverse. Correct, but allocates two full new strings just to answer a yes/no question.',
        steps: [
          'Scan the input, keeping only letters and digits (lowercased) in a new StringBuilder.',
          'Reverse that cleaned string.',
          'Compare the cleaned string to its reversed version.',
          'Return whether they\'re equal.',
        ],
        code:
`boolean isPalindromeBasic(String s) {
    StringBuilder cleaned = new StringBuilder();
    for (char c : s.toCharArray()) {
        if (Character.isLetterOrDigit(c)) {
            cleaned.append(Character.toLowerCase(c));
        }
    }
    String forward = cleaned.toString();
    String backward = cleaned.reverse().toString();
    return forward.equals(backward);
}`,
        complexity: { time: 'O(n)', space: 'O(n) — two new strings built' },
      },
      {
        name: 'Optimized — Two Pointers, Skip and Compare In Place',
        explanation: 'Walk two pointers inward from both ends of the ORIGINAL string, skipping over any non-alphanumeric character on the fly, and comparing case-insensitively. No new string is ever built.',
        steps: [
          'Start pointers at both ends of the string.',
          'Advance the left pointer past any non-alphanumeric characters; advance the right pointer past any non-alphanumeric characters from its side.',
          'Compare the two characters (case-insensitively). If they differ, it\'s not a palindrome.',
          'Move both pointers inward and repeat until they meet.',
        ],
        visual: {
          arrays: [{ label: 's = "race a car"', values: ['r', 'a', 'c', 'e', ' ', 'a', ' ', 'c', 'a', 'r'] }],
          varNames: [],
          steps: [
            { highlights: { 's = "race a car"': [0, 9] }, note: `'r' vs 'r' — match. Move inward.` },
            { highlights: { 's = "race a car"': [1, 8] }, note: `'a' vs 'a' — match. Move inward.` },
            { highlights: { 's = "race a car"': [2, 7] }, note: `'c' vs 'c' — match. Move inward.` },
            { highlights: { 's = "race a car"': [3, 5] }, note: `Right pointer skipped the space at index 6. Now comparing 'e' (index 3) vs 'a' (index 5) — mismatch! Not a palindrome.` },
          ],
        },
        code:
`boolean isPalindrome(String s) {
    int left = 0, right = s.length() - 1;
    while (left < right) {
        while (left < right && !Character.isLetterOrDigit(s.charAt(left))) left++;
        while (left < right && !Character.isLetterOrDigit(s.charAt(right))) right--;
        if (Character.toLowerCase(s.charAt(left)) != Character.toLowerCase(s.charAt(right))) {
            return false;
        }
        left++;
        right--;
    }
    return true;
}`,
        complexity: { time: 'O(n)', space: 'O(1) extra' },
      },
    ],
  },
  {
    id: 'longest-palindromic-substring-drill',
    title: 'Longest Palindromic Substring',
    level: 'Easy',
    crossReference:
      "This drill's full walkthrough — including a third, O(n) Manacher's Algorithm approach — lives in this " +
      "pattern's curated problem list — open \"Longest Palindromic Substring\" there for the complete step-by-step. " +
      "You can also try the expand-around-center approach hands-on in this pattern's Interactive Practice playground.",
  },
  {
    id: 'palindrome-number',
    title: 'Palindrome Number',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Convert to a String and Compare',
        explanation: 'Convert the integer to a string, and compare it to its own reverse. Correct and simple, but relies on string allocation for what is fundamentally a numeric question.',
        steps: [
          'Negative numbers can never be palindromes (the minus sign breaks symmetry) — return false immediately.',
          'Convert the number to a string.',
          'Reverse that string.',
          'Return whether the original and reversed strings are equal.',
        ],
        code:
`boolean isPalindromeBasic(int x) {
    if (x < 0) return false;
    String s = String.valueOf(x);
    return s.equals(new StringBuilder(s).reverse().toString());
}`,
        complexity: { time: 'O(digits)', space: 'O(digits) — string allocation' },
      },
      {
        name: 'Optimized — Reverse Only Half the Number',
        explanation: 'Peel digits off the END of the number using pure arithmetic, building up a "reversed half" — and stop the moment that reversed half catches up to (or exceeds) what remains of the original. No string is ever built, and only half the digits are ever touched.',
        steps: [
          'A negative number, or a positive number ending in 0 (other than 0 itself), can never be a palindrome — reject immediately.',
          'While the remaining original value is still larger than the reversed-half built so far, peel off the original\'s last digit and append it to the reversed half.',
          'Stop once the reversed half has caught up — for even-length numbers they\'ll be equal; for odd-length numbers the reversed half will have one extra (middle) digit.',
          'Compare the remaining original to the reversed half directly, or to the reversed half with its last digit dropped (for odd-length numbers).',
        ],
        visual: {
          arrays: [],
          varNames: ['x', 'reversedHalf'],
          steps: [
            { vars: { x: 121, reversedHalf: 0 }, note: 'Start: x=121, reversedHalf=0.' },
            { vars: { x: 12, reversedHalf: 1 }, note: 'x(121) > reversedHalf(0): reversedHalf = 0×10 + 121%10 = 1. x = 121/10 = 12.' },
            { vars: { x: 1, reversedHalf: 12 }, note: 'x(12) > reversedHalf(1): reversedHalf = 1×10 + 12%10 = 12. x = 12/10 = 1.' },
            { vars: { x: 1, reversedHalf: 12 }, note: 'x(1) is NOT > reversedHalf(12) — loop stops. Compare: x(1) == reversedHalf/10 (12/10=1) → true. Palindrome!' },
          ],
        },
        code:
`boolean isPalindrome(int x) {
    if (x < 0 || (x % 10 == 0 && x != 0)) return false;
    int reversedHalf = 0;
    while (x > reversedHalf) {
        reversedHalf = reversedHalf * 10 + x % 10;
        x /= 10;
    }
    return x == reversedHalf || x == reversedHalf / 10;
}`,
        complexity: { time: 'O(digits / 2)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'palindromic-substrings-drill',
    title: 'Palindromic Substrings',
    level: 'Medium',
    crossReference:
      "This drill's full Basic vs. Optimized walkthrough lives in this pattern's curated problem list — open " +
      "\"Palindromic Substrings\" there for the complete step-by-step. You can also try it hands-on in the " +
      "\"Palindromic Substrings\" mode of the Interactive Practice playground.",
  },
  {
    id: 'valid-palindrome-ii',
    title: 'Valid Palindrome II (At Most One Deletion)',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Try Removing Every Single Character',
        explanation: 'First check if the string is already a palindrome. If not, try removing each character ONE at a time, checking whether the resulting shorter string is a palindrome. Correct, but builds a new string and re-checks it fully for every possible removal position.',
        steps: [
          'Check whether the full string is already a palindrome — if so, done.',
          'Otherwise, for each index, build a new string with just that one character removed.',
          'Check whether that new string is a palindrome.',
          'Succeed the moment any single removal produces a valid palindrome.',
        ],
        code:
`boolean validPalindromeBasic(String s) {
    if (isPalindrome(s, 0, s.length() - 1)) return true;
    for (int i = 0; i < s.length(); i++) {
        String candidate = s.substring(0, i) + s.substring(i + 1);
        if (isPalindrome(candidate, 0, candidate.length() - 1)) return true;
    }
    return false;
}

private boolean isPalindrome(String s, int lo, int hi) {
    while (lo < hi) {
        if (s.charAt(lo++) != s.charAt(hi--)) return false;
    }
    return true;
}`,
        complexity: { time: 'O(n²)', space: 'O(n) per candidate string' },
      },
      {
        name: 'Optimized — Two Pointers, Branch Only at the FIRST Mismatch',
        explanation: 'Walk inward with two pointers as usual. The MOMENT a mismatch is found, there are only ever two possible fixes: skip the left character, or skip the right character. Checking both of those specific candidates is enough — no need to try removing every other position in the string.',
        steps: [
          'Walk two pointers inward from both ends, as in a normal palindrome check.',
          'The instant a mismatch is found, that\'s the ONLY point where a deletion could possibly help.',
          'Check whether skipping the left character (advancing left by one) completes a valid palindrome from there.',
          'Check whether skipping the right character (advancing right by one) does — if either succeeds, so does the original string with one deletion.',
        ],
        visual: {
          arrays: [{ label: 's = "abca"', values: ['a', 'b', 'c', 'a'] }],
          varNames: [],
          steps: [
            { highlights: { 's = "abca"': [0, 3] }, note: `'a' vs 'a' — match. Move inward.` },
            { highlights: { 's = "abca"': [1, 2] }, note: `'b' vs 'c' — mismatch! Try skipping the left ('b'): check range [2,2] — a single character, trivially a palindrome. Success — "abca" is valid with one deletion.` },
          ],
        },
        code:
`boolean validPalindrome(String s) {
    int left = 0, right = s.length() - 1;
    while (left < right) {
        if (s.charAt(left) != s.charAt(right)) {
            return isPalindromeRange(s, left + 1, right) || isPalindromeRange(s, left, right - 1);
        }
        left++;
        right--;
    }
    return true;
}

private boolean isPalindromeRange(String s, int lo, int hi) {
    while (lo < hi) {
        if (s.charAt(lo++) != s.charAt(hi--)) return false;
    }
    return true;
}`,
        complexity: { time: 'O(n)', space: 'O(1) extra' },
      },
    ],
  },
  {
    id: 'longest-palindromic-subsequence',
    title: 'Longest Palindromic Subsequence',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Brute Force Recursion',
        explanation: 'At each pair of end-pointers (i, j), if the characters match, recurse inward and add 2; otherwise, try dropping EITHER end and take the better result. Correct, but the same (i, j) range gets re-solved from scratch by many different earlier decisions.',
        steps: [
          'Base case: an empty range (i > j) contributes 0; a single character (i == j) contributes 1.',
          'If s[i] equals s[j], both characters can be part of the palindrome — recurse on the inner range and add 2.',
          'Otherwise, try dropping the left character, and separately dropping the right character — take whichever gives the longer result.',
          'The same range (i, j) recurs constantly across different branches without memoization.',
        ],
        code:
`int longestPalindromeSubseqBasic(String s, int i, int j) {
    if (i > j) return 0;
    if (i == j) return 1;
    if (s.charAt(i) == s.charAt(j)) {
        return 2 + longestPalindromeSubseqBasic(s, i + 1, j - 1);
    }
    return Math.max(
        longestPalindromeSubseqBasic(s, i + 1, j),
        longestPalindromeSubseqBasic(s, i, j - 1)
    );
}`,
        complexity: { time: 'O(2ⁿ)', space: 'O(n) — recursion depth' },
      },
      {
        name: 'Optimized — Bottom-Up 2-D DP',
        explanation: 'Build dp[i][j] — the longest palindromic subsequence within s[i..j] — filling by increasing substring length, from the bottom-right upward. Every dp[i][j] only ever needs dp[i+1][j-1], dp[i+1][j], and dp[i][j-1], all already computed.',
        steps: [
          'Every single character is a palindrome of length 1: dp[i][i] = 1.',
          'Fill outward: for each starting index i (from the end backward) and ending index j > i.',
          'If s[i] equals s[j], dp[i][j] = dp[i+1][j-1] + 2 — both ends join the inner palindrome.',
          'Otherwise, dp[i][j] = max(dp[i+1][j], dp[i][j-1]) — the better of dropping either end.',
        ],
        visual: {
          arrays: [
            { label: 's = "bbbab"', values: ['b', 'b', 'b', 'a', 'b'] },
            { label: 'dp diagonal — dp[i][i] = 1 for all i', values: [1, 1, 1, 1, 1] },
          ],
          varNames: [],
          steps: [
            { note: 'Every dp[i][i] = 1 (single characters). Then fill dp[2][4]: s[2]=\'b\' == s[4]=\'b\' → dp[2][4] = dp[3][3] + 2 = 1 + 2 = 3.' },
            { note: 'dp[1][2]: s[1]=\'b\' == s[2]=\'b\' → dp[1][2] = dp[2][1] (empty range, 0) + 2 = 2. dp[1][4]: s[1]=\'b\' == s[4]=\'b\' → dp[1][4] = dp[2][3] + 2. dp[2][3]: s[2]=\'b\' ≠ s[3]=\'a\' → dp[2][3] = max(dp[3][3], dp[2][2]) = 1. So dp[1][4] = 1 + 2 = 3.' },
            { note: 'dp[0][4]: s[0]=\'b\' == s[4]=\'b\' → dp[0][4] = dp[1][3] + 2. dp[1][3]: s[1]=\'b\' ≠ s[3]=\'a\' → dp[1][3] = max(dp[2][3], dp[1][2]) = max(1, 2) = 2. So dp[0][4] = 2 + 2 = 4 — final answer, matching the subsequence "bbbb".' },
          ],
        },
        code:
`int longestPalindromeSubseq(String s) {
    int n = s.length();
    int[][] dp = new int[n][n];
    for (int i = n - 1; i >= 0; i--) {
        dp[i][i] = 1;
        for (int j = i + 1; j < n; j++) {
            if (s.charAt(i) == s.charAt(j)) {
                dp[i][j] = dp[i + 1][j - 1] + 2;
            } else {
                dp[i][j] = Math.max(dp[i + 1][j], dp[i][j - 1]);
            }
        }
    }
    return dp[0][n - 1];
}`,
        complexity: { time: 'O(n²)', space: 'O(n²)' },
      },
    ],
  },
  {
    id: 'palindrome-partitioning-min-cuts',
    title: 'Palindrome Partitioning II (Minimum Cuts)',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Brute Force Recursion',
        explanation: 'From each starting position, try every possible next palindromic piece, and recursively find the minimum cuts needed for the rest — checking palindrome-ness from scratch at every candidate. Correct, but massively overlapping subproblems without memoization.',
        steps: [
          'From the current start position, try every possible end position for the next piece.',
          'If that piece is a palindrome, recursively find the minimum cuts needed for everything after it.',
          'Take the minimum across all valid pieces, adding 1 for the cut just made.',
          'The same "minimum cuts starting from position i" question gets re-solved repeatedly.',
        ],
        code:
`int minCutBasic(String s, int start) {
    if (start == s.length()) return -1;
    int minCuts = Integer.MAX_VALUE;
    for (int end = start; end < s.length(); end++) {
        if (isPalindrome(s, start, end)) {
            int cuts = 1 + minCutBasic(s, end + 1);
            minCuts = Math.min(minCuts, cuts);
        }
    }
    return minCuts;
}

private boolean isPalindrome(String s, int lo, int hi) {
    while (lo < hi) {
        if (s.charAt(lo++) != s.charAt(hi--)) return false;
    }
    return true;
}`,
        complexity: { time: 'Exponential in the worst case', space: 'O(n) — recursion depth' },
      },
      {
        name: 'Optimized — Precompute Palindromes, Then 1-D DP on Cuts',
        explanation: 'First build a full isPalindrome[i][j] table ONCE, bottom-up (so every check is O(1)). Then a separate 1-D DP, dp[i] = minimum cuts needed for the prefix ending at i, only considers j\'s where isPalindrome[j][i] is already known.',
        steps: [
          'Build isPalindrome[i][j] for every substring, bottom-up: true if s[i]==s[j] and (the substring is short, or the inner part is also a palindrome).',
          'dp[i] = 0 whenever the whole prefix s[0..i] is itself a palindrome — no cut needed.',
          'Otherwise, scan every j ≤ i where isPalindrome[j][i] is true, and take dp[i] = min(dp[i], dp[j-1] + 1).',
          'Return dp[n-1].',
        ],
        visual: {
          arrays: [{ label: 's = "aab"', values: ['a', 'a', 'b'] }],
          varNames: [],
          steps: [
            { note: 'isPalindrome table built first: [0][0]=T, [1][1]=T, [2][2]=T, [0][1]=T ("aa"), [1][2]=F ("ab"), [0][2]=F ("aab").' },
            { note: 'dp[0]: isPalindrome[0][0]=T → dp[0]=0 (no cut needed for "a").' },
            { note: 'dp[1]: isPalindrome[0][1]=T ("aa" is a palindrome) → dp[1]=0 (no cut needed at all).' },
            { note: 'dp[2]: isPalindrome[0][2]=F. Check j=1: isPalindrome[1][2]=F, skip. Check j=2: isPalindrome[2][2]=T → dp[2] = dp[1]+1 = 0+1 = 1. Final answer: 1 cut ("aa" | "b").' },
          ],
        },
        code:
`int minCut(String s) {
    int n = s.length();
    boolean[][] isPalindrome = new boolean[n][n];
    for (int i = n - 1; i >= 0; i--) {
        for (int j = i; j < n; j++) {
            isPalindrome[i][j] = s.charAt(i) == s.charAt(j) && (j - i < 2 || isPalindrome[i + 1][j - 1]);
        }
    }

    int[] dp = new int[n];
    for (int i = 0; i < n; i++) {
        if (isPalindrome[0][i]) { dp[i] = 0; continue; }
        dp[i] = Integer.MAX_VALUE;
        for (int j = 1; j <= i; j++) {
            if (isPalindrome[j][i]) {
                dp[i] = Math.min(dp[i], dp[j - 1] + 1);
            }
        }
    }
    return dp[n - 1];
}`,
        complexity: { time: 'O(n²)', space: 'O(n²) for the palindrome table' },
      },
    ],
  },
  {
    id: 'shortest-palindrome',
    title: 'Shortest Palindrome',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Try Every Prefix Length',
        explanation: 'Starting from the longest possible prefix and working shorter, check each one for being a palindrome. The first (longest) palindromic prefix found determines how much of the string can stay untouched — everything after it must be mirrored and prepended.',
        steps: [
          'Starting from the full string length down to 1, check whether the prefix of that length is a palindrome.',
          'The FIRST (longest) palindromic prefix found is the answer — no shorter prefix needs checking.',
          'Take the remaining suffix (everything after that prefix), reverse it, and prepend it to the original string.',
          'Return the combined result.',
        ],
        code:
`String shortestPalindromeBasic(String s) {
    int n = s.length();
    for (int end = n; end >= 1; end--) {
        if (isPalindrome(s, 0, end - 1)) {
            String suffixToPrepend = new StringBuilder(s.substring(end)).reverse().toString();
            return suffixToPrepend + s;
        }
    }
    return s;
}

private boolean isPalindrome(String s, int lo, int hi) {
    while (lo < hi) {
        if (s.charAt(lo++) != s.charAt(hi--)) return false;
    }
    return true;
}`,
        complexity: { time: 'O(n²)', space: 'O(n) for candidate strings' },
      },
      {
        name: 'Optimized — KMP Failure Function on s + "#" + reverse(s)',
        explanation: 'Build a combined string: the original, a separator that can\'t appear in the input, then the REVERSED original. Running the KMP failure-function computation on this combined string finds the longest prefix of the ORIGINAL that is also a suffix of its reverse — which is exactly the longest palindromic prefix of the original string.',
        steps: [
          'Build combined = s + "#" + reverse(s).',
          'Compute the KMP failure function (longest proper prefix that\'s also a suffix) across the entire combined string.',
          'The failure function\'s value at the LAST position tells the length of the longest palindromic prefix of s.',
          'Reverse everything after that prefix, and prepend it to s.',
        ],
        visual: {
          arrays: [],
          varNames: [],
          steps: [
            { note: 's = "aacecaaa". combined = "aacecaaa" + "#" + "aaacecaa" (reverse of s).' },
            { note: 'Running the KMP failure function across all 17 characters of combined builds up progressively — matching prefixes of s against suffixes of the combined string as it scans.' },
            { note: 'The final failure-function value comes out to 7 — meaning the first 7 characters of s, "aacecaa", form the longest palindromic prefix.' },
            { note: 'The remaining suffix is just "a" (the last character). Reversed, it\'s still "a". Prepending gives: "a" + "aacecaaa" = "aaacecaaa" — the shortest possible palindrome.' },
          ],
        },
        code:
`String shortestPalindrome(String s) {
    String combined = s + "#" + new StringBuilder(s).reverse().toString();
    int[] fail = new int[combined.length()];
    for (int i = 1; i < combined.length(); i++) {
        int j = fail[i - 1];
        while (j > 0 && combined.charAt(i) != combined.charAt(j)) {
            j = fail[j - 1];
        }
        if (combined.charAt(i) == combined.charAt(j)) j++;
        fail[i] = j;
    }
    int longestPalinPrefixLen = fail[combined.length() - 1];
    String suffixToPrepend = new StringBuilder(s.substring(longestPalinPrefixLen)).reverse().toString();
    return suffixToPrepend + s;
}`,
        complexity: { time: 'O(n)', space: 'O(n) for the combined string and failure array' },
      },
    ],
  },
  {
    id: 'palindrome-permutation',
    title: 'Palindrome Permutation',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Try Every Permutation',
        explanation: 'Generate every possible rearrangement of the string\'s characters, and check whether ANY of them is a palindrome. Technically correct, but factorial time makes it impractical beyond the tiniest inputs.',
        steps: [
          'Recursively generate every permutation of the string\'s characters.',
          'For each complete permutation, check whether it reads the same forward and backward.',
          'Succeed the moment any permutation is a palindrome.',
          'With n! total permutations, this becomes impossibly slow past a handful of characters.',
        ],
        code:
`boolean canPermutePalindromeBasic(String s) {
    return hasPalindromePermutation(s.toCharArray(), 0);
}

private boolean hasPalindromePermutation(char[] chars, int start) {
    if (start == chars.length) return isPalindrome(chars);
    for (int i = start; i < chars.length; i++) {
        swap(chars, start, i);
        if (hasPalindromePermutation(chars, start + 1)) return true;
        swap(chars, start, i);
    }
    return false;
}

private boolean isPalindrome(char[] chars) {
    int lo = 0, hi = chars.length - 1;
    while (lo < hi) {
        if (chars[lo++] != chars[hi--]) return false;
    }
    return true;
}

private void swap(char[] chars, int i, int j) {
    char temp = chars[i]; chars[i] = chars[j]; chars[j] = temp;
}`,
        complexity: { time: 'O(n! · n)', space: 'O(n) — recursion depth' },
      },
      {
        name: 'Optimized — Count Character Frequencies, Check Odd Counts',
        explanation: 'A palindrome permutation exists if and only if AT MOST ONE character has an odd frequency count — every character with an even count can be split symmetrically on both sides, and at most one "leftover" character can sit in the exact middle.',
        steps: [
          'Count how many times each character appears in the string.',
          'Count how many DISTINCT characters have an odd frequency.',
          'If that count is 0 or 1, a palindrome permutation exists.',
          'If two or more characters have odd frequencies, no arrangement can make a valid palindrome.',
        ],
        visual: {
          arrays: [{ label: 's = "aab"', values: ['a', 'a', 'b'] }],
          varNames: ['oddCount'],
          steps: [
            { vars: { oddCount: 0 }, note: `Frequencies: 'a' → 2 (even), 'b' → 1 (odd). oddCount starts at 0.` },
            { vars: { oddCount: 1 }, note: `'b' has an odd count → oddCount becomes 1.` },
            { vars: { oddCount: 1 }, note: `oddCount(1) ≤ 1 — a palindrome permutation exists: "aba". (Contrast with s="abc": all three counts are odd, oddCount=3 > 1 — no palindrome permutation possible.)` },
          ],
        },
        code:
`boolean canPermutePalindrome(String s) {
    int[] freq = new int[128];
    for (char c : s.toCharArray()) freq[c]++;
    int oddCount = 0;
    for (int f : freq) {
        if (f % 2 != 0) oddCount++;
    }
    return oddCount <= 1;
}`,
        complexity: { time: 'O(n)', space: 'O(1) — a fixed 128-entry array' },
      },
    ],
  },
];

export default palindromeDrills;
